import { tmdbRequest } from './tmdb';

export const OTT_FETCH_WINDOW_DAYS = 30;
export const OTT_NEW_WINDOW_DAYS = 5;
export const OTT_MAX_PAGE = 500;

// Per-page share of each language lane. The OTT row started Hindi-only; South
// and Hollywood titles are included too because they are watched with Hindi
// audio in this app. Keeping the caps means a busy Tamil/Telugu week cannot
// push Bollywood off the row.
export const OTT_LANGUAGE_QUOTAS = [
  { lang: 'hi', cap: 10 }, // Bollywood
  { lang: 'ta', cap: 2 },  // South — Tamil
  { lang: 'te', cap: 2 },  // South — Telugu
  { lang: 'kn', cap: 2 },  // South — Kannada
  { lang: 'ml', cap: 1 },  // South — Malayalam
  { lang: 'en', cap: 5 },  // Hollywood
];

const OTT_LANGUAGE_LABELS = {
  ta: 'South · Hindi',
  te: 'South · Hindi',
  kn: 'South · Hindi',
  ml: 'South · Hindi',
  en: 'Hollywood · Hindi',
};

const quotaFor = (lang) => {
  const found = OTT_LANGUAGE_QUOTAS.find((entry) => entry.lang === lang);
  return found ? found.cap : 0;
};

const languageLabel = (lang) => OTT_LANGUAGE_LABELS[lang] || '';

const METADATA_CACHE_TTL_MS = 6 * 60 * 60 * 1000;
const METADATA_CONCURRENCY = 6;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const metadataCache = new Map();

export const clearOttCache = () => metadataCache.clear();

const toIsoDate = (date) => date.toISOString().slice(0, 10);
const todayIso = () => toIsoDate(new Date());
const daysAgoIso = (days) => toIsoDate(new Date(Date.now() - days * MS_PER_DAY));

const readCache = (id) => {
  const entry = metadataCache.get(id);
  if (!entry) return null;
  if (entry.expiresAt < Date.now()) {
    metadataCache.delete(id);
    return null;
  }
  return entry.value;
};

const writeCache = (id, value) =>
  metadataCache.set(id, { value, expiresAt: Date.now() + METADATA_CACHE_TTL_MS });

const providerNames = (region) => {
  const groups = ['flatrate', 'free', 'ads', 'rent', 'buy'];
  for (let i = 0; i < groups.length; i += 1) {
    const list = region && region[groups[i]];
    if (Array.isArray(list) && list.length > 0) {
      return list.map((provider) => provider.provider_name).filter(Boolean);
    }
  }
  return [];
};

const digitalRelease = (releaseDates, regionCode = 'IN') => {
  const region = (releaseDates?.results || []).find(
    (entry) => entry.iso_3166_1 === regionCode
  );
  if (!region || !Array.isArray(region.release_dates)) return null;

  return (
    region.release_dates
      .filter((entry) => Number(entry.type) === 4)
      .sort((a, b) => String(b.release_date || '').localeCompare(String(a.release_date || '')))[0] ||
    null
  );
};

const platformNote = (note) => {
  const value = String(note || '').trim();
  if (!value || value.length > 40) return '';
  return /^[\w .&+'-]+$/.test(value) ? value : '';
};

export const isNewOttRelease = (ottDate, now = Date.now()) => {
  const timestamp = Date.parse(ottDate);
  if (Number.isNaN(timestamp)) return false;
  const days = Math.floor((now - timestamp) / MS_PER_DAY);
  return days >= 0 && days <= OTT_NEW_WINDOW_DAYS;
};

const enrichMovie = async (movie) => {
  const cached = readCache(movie.id);
  if (cached) return cached;

  const [releaseDates, providers] = await Promise.allSettled([
    tmdbRequest(`/movie/${movie.id}/release_dates`),
    tmdbRequest(`/movie/${movie.id}/watch/providers`),
  ]);

  const digital =
    releaseDates.status === 'fulfilled' ? digitalRelease(releaseDates.value?.data) : null;
  const names =
    providers.status === 'fulfilled'
      ? providerNames(providers.value?.data?.results?.IN)
      : [];

  const value = {
    ott_name: names[0] || platformNote(digital?.note) || '',
    ott_date: digital ? String(digital.release_date || '').slice(0, 10) : '',
  };

  if (digital) writeCache(movie.id, value);
  return value;
};

const mapWithConcurrency = async (items, limit, worker) => {
  const results = new Array(items.length);
  let cursor = 0;

  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await worker(items[index], index);
    }
  });

  await Promise.all(runners);
  return results;
};

export const fetchOttFeedPage = async ({ page = 1 } = {}) => {
  const targetPage = Math.min(Math.max(1, Number(page) || 1), OTT_MAX_PAGE);

  // /discover accepts only ONE with_original_language, so fan out per language
  // and merge the streams (same approach as the main feed).
  const settled = await Promise.allSettled(
    OTT_LANGUAGE_QUOTAS.map(({ lang }) =>
      tmdbRequest('/discover/movie', {
        with_original_language: lang,
        with_release_type: '4',
        region: 'IN',
        'release_date.gte': daysAgoIso(OTT_FETCH_WINDOW_DAYS),
        'release_date.lte': todayIso(),
        sort_by: 'primary_release_date.desc',
        page: targetPage,
      })
    )
  );

  const fulfilled = settled.filter((result) => result.status === 'fulfilled');
  if (fulfilled.length === 0) throw settled[0].reason;

  const streams = fulfilled.map((result) => result.value?.data ?? {});
  const rawMovies = streams.flatMap((data) =>
    Array.isArray(data?.results) ? data.results : []
  );

  // A title can appear in more than one stream on rare TMDB data glitches.
  const seenIds = new Set();
  const uniqueMovies = rawMovies.filter((movie) => {
    if (!movie?.id || seenIds.has(movie.id)) return false;
    seenIds.add(movie.id);
    return true;
  });

  // Cap every language lane before enrichment so the metadata fan-out stays
  // small (<= 22 calls per page) and one language cannot swamp the row.
  const perLanguage = new Map();
  uniqueMovies.forEach((movie) => {
    const lang = movie?.original_language;
    if (!lang || (lang !== 'hi' && !OTT_LANGUAGE_LABELS[lang])) return;
    const bucket = perLanguage.get(lang) || [];
    bucket.push(movie);
    perLanguage.set(lang, bucket);
  });

  const movies = [];
  perLanguage.forEach((bucket, lang) => {
    const cap = quotaFor(lang);
    if (cap > 0) movies.push(...bucket.slice(0, cap));
  });

  const enriched = await mapWithConcurrency(movies, METADATA_CONCURRENCY, async (movie) => {
    const meta = await enrichMovie(movie);
    const ottDate = meta.ott_date || movie.release_date || '';
    return {
      ...movie,
      media_type: 'movie',
      ott_name: meta.ott_name,
      ott_date: ottDate,
      ott_new: isNewOttRelease(ottDate),
      language_label: languageLabel(movie.original_language),
    };
  });

  const items = enriched
    .filter(Boolean)
    .sort((a, b) => String(b.ott_date || '').localeCompare(String(a.ott_date || '')));

  const totalPages = Math.min(
    Math.max(...streams.map((data) => Number(data?.total_pages) || 1), targetPage),
    OTT_MAX_PAGE
  );
  const totalResults = streams.reduce(
    (sum, data) => sum + (Number(data?.total_results) || 0),
    0
  );

  return {
    items,
    page: targetPage,
    totalPages,
    totalResults: totalResults || items.length,
    hasMore:
      uniqueMovies.length > 0 &&
      targetPage < OTT_MAX_PAGE &&
      streams.some((data) => targetPage < (Number(data?.total_pages) || 1)),
  };
};

export const ottFeed = {
  fetch: (options = {}) => fetchOttFeedPage(options),
};

export default ottFeed;
